<template>
  <v-dialog :value="value" max-width="860" scrollable @input="$emit('input', $event)" @keydown.esc="close">
    <v-card class="rounded-xl" style="overflow: hidden;">

      <!-- Header -->
      <div class="dsm-header">
        <div>
          <h3 class="dsm-title">Настройки диагностики</h3>
          <p class="dsm-subtitle">Привяжите кейсы к компетенциям и выберите, какие блоки попадут в отчёт.</p>
        </div>
        <v-btn icon small @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </div>

      <!-- Tabs -->
      <div class="dsm-tabs">
        <button
          class="dsm-tab"
          :class="{ 'dsm-tab--active': activeTab === 'cases' }"
          @click="activeTab = 'cases'"
        >Кейсы</button>
        <button
          class="dsm-tab"
          :class="{ 'dsm-tab--active': activeTab === 'report' }"
          @click="activeTab = 'report'"
        >Отчёт</button>
      </div>

      <!-- Body -->
      <v-card-text class="pa-0" style="overflow: hidden; flex: 1;">

        <!-- ═══ CASES TAB ═══ -->
        <template v-if="activeTab === 'cases'">
          <div class="dsm-body">

            <!-- Left sidebar: competency list -->
            <div class="dsm-sidebar">
              <div
                v-for="(comp, idx) in competencies"
                :key="comp.id || idx"
                class="dsm-sidebar__item"
                :class="{ 'dsm-sidebar__item--active': activeCompIdx === idx }"
                @click="activeCompIdx = idx"
              >
                <span class="dsm-sidebar__item-name">{{ comp.name }}</span>
                <span v-if="localCasesMap[idx] && localCasesMap[idx].length" class="dsm-sidebar__badge">
                  {{ localCasesMap[idx].length }}
                </span>
              </div>
            </div>

            <!-- Right panel -->
            <div class="dsm-panel" v-if="activeComp">

              <!-- Competency info -->
              <h4 class="dsm-panel__title">{{ activeComp.name }}</h4>
              <p class="dsm-panel__desc">{{ activeComp.description }}</p>

              <!-- Library cases -->
              <div class="dsm-section-label">
                <v-icon size="15" color="#9e9e9e" class="mr-1">mdi-book-open-variant</v-icon>
                ИЗ БИБЛИОТЕКИ
              </div>

              <template v-if="libraryCasesLoading">
                <v-skeleton-loader type="list-item-two-line" class="mb-2"/>
                <v-skeleton-loader type="list-item-two-line" class="mb-2"/>
              </template>

              <template v-else>
                <template v-if="filteredLibraryCases.length">
                  <div
                    v-for="libCase in filteredLibraryCases"
                    :key="libCase.id"
                    class="dsm-case-card"
                  >
                    <div class="dsm-case-card__body">
                      <p class="dsm-case-card__name">{{ libCase.name }}</p>
                      <p class="dsm-case-card__desc">{{ libCase.description }}</p>
                    </div>
                    <v-btn
                      v-if="!isAttached(libCase)"
                      depressed
                      small
                      outlined
                      color="#212121"
                      class="dsm-case-card__btn flex-shrink-0"
                      @click="addLibraryCase(libCase)"
                    >Добавить</v-btn>
                    <div v-else class="dsm-added-badge">
                      <v-icon x-small color="#009E60">mdi-check</v-icon>
                      Добавлен
                    </div>
                  </div>
                </template>
                <div v-else class="dsm-empty-text mb-4">
                  Нет кейсов в библиотеке для этой компетенции.
                </div>
              </template>

              <!-- Attached cases -->
              <div class="dsm-section-label mt-3">
                <span style="flex:1;">ПРИВЯЗАННЫЕ КЕЙСЫ</span>
                <v-btn
                  depressed
                  x-small
                  outlined
                  color="#009E60"
                  style="border-radius:6px;"
                  @click="showCustomForm = !showCustomForm"
                >
                  <v-icon x-small left>mdi-plus</v-icon>
                  Свой кейс
                </v-btn>
              </div>

              <!-- Custom case form -->
              <div v-if="showCustomForm" class="dsm-custom-form">
                <v-text-field
                  v-model="customCase.name"
                  outlined dense hide-details
                  placeholder="Название кейса"
                  class="mb-2"
                />
                <v-textarea
                  v-model="customCase.description"
                  outlined dense hide-details
                  placeholder="Описание кейса (необязательно)"
                  rows="2" no-resize
                  class="mb-2"
                />
                <div class="d-flex justify-end" style="gap:8px;">
                  <v-btn text small @click="cancelCustomForm">Отмена</v-btn>
                  <v-btn
                    depressed small color="#009E60" dark
                    :disabled="!customCase.name.trim()"
                    @click="addCustomCase"
                  >Добавить</v-btn>
                </div>
              </div>

              <!-- Attached list -->
              <div v-if="attachedCases.length">
                <div
                  v-for="(aCase, i) in attachedCases"
                  :key="aCase.uid || aCase.id || i"
                  class="dsm-case-card dsm-case-card--attached"
                  :class="{ 'dsm-case-card--pending': aCase.pending }"
                >
                  <div class="dsm-case-card__body">
                    <p class="dsm-case-card__name">
                      {{ aCase.name }}
                      <v-chip v-if="aCase.pending" x-small color="#e8f5e9" class="ml-1" style="height:16px;">новый</v-chip>
                    </p>
                    <p class="dsm-case-card__desc">{{ aCase.description }}</p>
                  </div>
                  <v-btn icon x-small @click="removeCase(i)">
                    <v-icon small color="#bdbdbd">mdi-close</v-icon>
                  </v-btn>
                </div>
              </div>

              <div v-else class="dsm-empty-attached">
                Нет кейсов. Добавьте из библиотеки или создайте свой.
              </div>

            </div>
          </div>
        </template>

        <!-- ═══ REPORT TAB (stub) ═══ -->
        <template v-else>
          <div class="pa-5">
            <v-row>
              <v-col v-for="module in reportModules" :key="module.key" cols="12" sm="6">
                <div
                  class="dsm-module-card"
                  :class="{ 'dsm-module-card--selected': selectedModules.includes(module.key) }"
                  @click="toggleModule(module.key)"
                >
                  <div class="d-flex align-start" style="gap: 10px;">
                    <v-icon :color="selectedModules.includes(module.key) ? '#009E60' : '#c0c0c0'">
                      {{ selectedModules.includes(module.key) ? 'mdi-check-circle' : 'mdi-circle-outline' }}
                    </v-icon>
                    <div>
                      <div class="font-weight-bold text-body-2 grey--text text--darken-3">{{ module.label }}</div>
                      <div class="text-caption grey--text">{{ module.description }}</div>
                    </div>
                  </div>
                </div>
              </v-col>
            </v-row>
          </div>
        </template>

      </v-card-text>

      <!-- Footer -->
      <v-divider/>
      <div class="dsm-footer">
        <v-btn outlined color="grey darken-1" height="40" style="min-width:100px;" :disabled="saving" @click="close">
          Отмена
        </v-btn>
        <v-btn
          color="#009E60" dark depressed height="40" style="min-width:120px;"
          :loading="saving"
          @click="save"
        >
          Сохранить
        </v-btn>
      </div>

    </v-card>
  </v-dialog>
</template>

<script>
import { mapGetters } from 'vuex';

let uid = 0;

export default {
  name: 'DiagnosticSettingsModal',

  props: {
    value: { type: Boolean, default: false },
    competencies: { type: Array, default: () => [] },
    libraryCases: { type: Array, default: () => [] },
    libraryCasesLoading: { type: Boolean, default: false },
    saving: { type: Boolean, default: false },
    resultSettings: { type: Object, default: null },
  },

  data: () => ({
    activeTab: 'cases',
    activeCompIdx: 0,
    localCasesMap: {},
    showCustomForm: false,
    customCase: { name: '', description: '' },
    selectedModules: [],
    initialSelectedModules: [],
  }),

  computed: {
    ...mapGetters({
      reportModules: 'diagnostics/diagnotics/reportModules',
    }),
    activeComp() {
      return this.competencies[this.activeCompIdx] || null;
    },
    attachedCases() {
      return this.localCasesMap[this.activeCompIdx] || [];
    },
    filteredLibraryCases() {
      if (!this.activeComp) return [];
      const templateId = this.activeComp.source_competence_template;
      if (!templateId) return this.libraryCases;
      return this.libraryCases.filter(c => c.competence === templateId);
    },
  },

  watch: {
    value(open) {
      if (open) this.reset();
    },
  },

  methods: {
    reset() {
      this.activeTab = 'cases';
      this.activeCompIdx = 0;
      this.showCustomForm = false;
      this.customCase = { name: '', description: '' };
      const map = {};
      this.competencies.forEach((comp, idx) => {
        map[idx] = (comp.cases || []).map(c => ({ ...c }));
      });
      this.localCasesMap = map;
      const s = this.resultSettings || {};
      this.selectedModules = this.reportModules
        .filter(m => s[m.key] === true)
        .map(m => m.key);
      this.initialSelectedModules = [...this.selectedModules];
    },

    close() {
      this.$emit('input', false);
    },

    isAttached(libCase) {
      return (this.attachedCases).some(
        c => c.source_case_template === libCase.id || c.id === libCase.id,
      );
    },

    addLibraryCase(libCase) {
      const idx = this.activeCompIdx;
      const list = [...(this.localCasesMap[idx] || [])];
      if (this.isAttached(libCase)) return;
      list.push({
        uid: uid += 1,
        pending: true,
        source_case_template: libCase.id,
        name: libCase.name,
        description: libCase.description || '',
      });
      this.$set(this.localCasesMap, idx, list);
    },

    addCustomCase() {
      if (!this.customCase.name.trim()) return;
      const idx = this.activeCompIdx;
      const list = [...(this.localCasesMap[idx] || [])];
      list.push({
        uid: uid += 1,
        pending: true,
        source_case_template: null,
        name: this.customCase.name.trim(),
        description: this.customCase.description.trim(),
      });
      this.$set(this.localCasesMap, idx, list);
      this.cancelCustomForm();
    },

    cancelCustomForm() {
      this.showCustomForm = false;
      this.customCase = { name: '', description: '' };
    },

    removeCase(i) {
      const idx = this.activeCompIdx;
      const list = [...(this.localCasesMap[idx] || [])];
      list.splice(i, 1);
      this.$set(this.localCasesMap, idx, list);
    },

    save() {
      const adds = [];
      const removes = [];

      this.competencies.forEach((comp, idx) => {
        const original = new Map((comp.cases || []).map(c => [c.id, c]));
        const current = this.localCasesMap[idx] || [];

        // items to remove — in original but not in current
        const currentIds = new Set(current.filter(c => c.id).map(c => c.id));
        original.forEach((c, id) => {
          if (!currentIds.has(id)) removes.push({ caseId: id });
        });

        // items to add — pending (no real id yet)
        current.filter(c => c.pending).forEach(c => {
          adds.push({
            competenceId: comp.id,
            name: c.name,
            description: c.description || '',
            sourceCaseTemplateId: c.source_case_template || null,
          });
        });
      });

      const modulesChanged = [...this.selectedModules].sort().join(',')
        !== [...this.initialSelectedModules].sort().join(',');

      this.$emit('save', {
        adds,
        removes,
        selectedModules: modulesChanged ? this.selectedModules : undefined,
      });
    },
    toggleModule(key) {
      const idx = this.selectedModules.indexOf(key);
      if (idx === -1) this.selectedModules.push(key);
      else this.selectedModules.splice(idx, 1);
    },
  },
};
</script>

<style scoped>
.dsm-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 20px 24px 12px;
}
.dsm-title {
  font-size: 17px;
  font-weight: 700;
  color: #212121;
  margin: 0 0 4px;
}
.dsm-subtitle {
  font-size: 13px;
  color: var(--v-primary-base, #009E60);
  margin: 0;
}

.dsm-tabs {
  display: flex;
  justify-content: center;
  gap: 4px;
  padding: 0 24px;
  background: #f5f5f5;
  border-top: 1px solid #e0e0e0;
  border-bottom: 1px solid #e0e0e0;
}
.dsm-tab {
  padding: 10px 20px;
  font-size: 14px;
  font-weight: 500;
  color: #757575;
  background: transparent;
  border: none;
  cursor: pointer;
  border-bottom: 2px solid transparent;
  transition: color 0.15s, border-color 0.15s;
  outline: none;
}
.dsm-tab--active {
  color: #212121;
  border-bottom-color: #212121;
}

.dsm-body {
  display: flex;
  height: 460px;
  overflow: hidden;
}

.dsm-sidebar {
  width: 210px;
  flex-shrink: 0;
  border-right: 1px solid #e0e0e0;
  overflow-y: auto;
  padding: 8px 0;
}
.dsm-sidebar__item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  font-size: 13px;
  color: #424242;
  cursor: pointer;
  transition: background 0.12s;
  line-height: 1.4;
  gap: 6px;
}
.dsm-sidebar__item:hover { background: #f5f5f5; }
.dsm-sidebar__item--active {
  background: #e8f5e9;
  color: #212121;
  font-weight: 600;
}
.dsm-sidebar__item-name { flex: 1; }
.dsm-sidebar__badge {
  font-size: 11px;
  font-weight: 700;
  background: #009E60;
  color: #fff;
  border-radius: 10px;
  padding: 1px 6px;
  flex-shrink: 0;
}

.dsm-panel {
  flex: 1;
  overflow-y: auto;
  padding: 20px 20px 12px;
}
.dsm-panel__title {
  font-size: 15px;
  font-weight: 700;
  color: #212121;
  margin: 0 0 4px;
}
.dsm-panel__desc {
  font-size: 13px;
  color: #9e9e9e;
  margin: 0 0 16px;
}

.dsm-section-label {
  display: flex;
  align-items: center;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  color: #9e9e9e;
  margin-bottom: 10px;
}

.dsm-case-card {
  display: flex;
  align-items: center;
  gap: 12px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 10px 14px;
  margin-bottom: 8px;
  background: #fff;
}
.dsm-case-card--attached { background: #fafafa; }
.dsm-case-card--pending { border-color: #a5d6a7; background: #f9fff9; }
.dsm-case-card__body { flex: 1; min-width: 0; }
.dsm-case-card__name {
  font-size: 13px;
  font-weight: 600;
  color: #212121;
  margin: 0 0 2px;
  display: flex;
  align-items: center;
}
.dsm-case-card__desc { font-size: 12px; color: #757575; margin: 0; }
.dsm-case-card__btn {
  font-size: 12px !important;
  text-transform: none !important;
  letter-spacing: 0 !important;
}

.dsm-added-badge {
  display: flex;
  align-items: center;
  gap: 3px;
  font-size: 12px;
  color: #009E60;
  font-weight: 500;
  flex-shrink: 0;
}

.dsm-custom-form {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 10px;
  background: #fafafa;
}

.dsm-empty-text { font-size: 12px; color: #bdbdbd; }
.dsm-empty-attached {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 20px;
  text-align: center;
  font-size: 13px;
  color: #bdbdbd;
}

.dsm-report-stub {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 460px;
  gap: 12px;
}
.dsm-report-stub__text { font-size: 14px; color: #bdbdbd; margin: 0; }

.dsm-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 14px 24px;
}

/* Report module cards */
.dsm-module-card {
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 12px 14px;
  cursor: pointer;
  transition: border-color 0.15s, background-color 0.15s;
  background: #fff;
  height: 100%;

  &:hover {
    border-color: #009E60;
    background-color: #f0fdf8;
  }

  &--selected {
    border-color: #009E60;
    background-color: #f0fdf8;
  }
}

</style>
